// Stays whole: PageText is one component whose TinyMCE handlers and load phase share its refs.
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react'
import { createPortal } from 'react-dom'
import { shouldCommitBlurContent } from './blurCommitPolicy'
import {
  itemSelector,
  caseSelector,
  dateFormatAtom,
  rightMenuAtom,
  caseFuneralHomeAtom,
  selectedThemeAtom,
} from 'atoms'
import {
  itemAtomFamily,
  pageSetLocaleAtom,
  swapServiceTemplateSignalAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import {
  Button,
  Flex,
  FullscreenModal,
  ServiceTemplateCreateModal,
  Text,
  TinyMCE,
} from 'components'
import {
  useRecoilValue,
  useSetRecoilState,
  useRecoilState,
  useRecoilCallback,
} from 'recoil'
import { useItemControlState } from '@/hooks/editor/useItemControlState'
import {
  showConvertTextModalForItemIdAtom,
  showServiceTemplateModalForItemIdAtom,
  swapServiceTemplateForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { updateDynamicText } from 'utils'
import { useAuthorCaseFields } from '@/hooks/editor/useAuthorCaseField'
import { EDITOR_FIELD_NAME_OF_DECEASED } from '@/utils/editor-text-box/editorTextBoxAdapter'
import * as S from './PageText.styled'
import { useSetToast } from 'hooks'
import {
  fitTextIntoBox,
  measureTextDimensions,
  useResizeObserver as resizeObserver,
} from 'hooks/editor/useTextAutosize'
import {
  GRACE_DISTANCE,
  pxOf,
  textBoxLoadFitStamp,
  textBoxLoadHeight,
} from '@/utils/editor/textBoxLoadHeight'
import { useEditorTextBoxGrowthCeiling } from '@/hooks/editor/useEditorTextBoxGrowthCeiling'
import { useDebugEffect } from 'hooks/debug/useDebugEffect'
import hasCaseDetailAttribute from 'utils/caseDetails/hasCaseDetailAttribute'
import { toggleEditableBlocks } from 'utils/tinyMCE/updateDynamicText/toggleEditableBlocks'
import { sanitizeDynamicText } from 'utils/tinyMCE/updateDynamicText/sanitizeDynamicText'
import {
  dynamicTextDependencies,
  dynamicTextInputsSignature,
} from 'utils/tinyMCE/updateDynamicText/dynamicTextDependencies'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useBleedWarning } from '@/hooks/editor/useBleedWarning'
import { reportPreviewLoadWarning } from '@/utils/editor/previewBleedReport'
import { logTextBoxLoadResult } from '@/utils/editor/textBoxLoadLog'
import { fontSizesPtOf } from '@/utils/editor/textGeometryProbe'
import { useTextContentBounds } from '@/hooks/editor/useTextContentBounds'
import { useServiceTemplates } from '@/hooks/editor/useServiceTemplates'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'
import {
  TEXT_ANATOMY_ATTR,
  TEXT_BLOCKS_ATTR,
  TEXT_BOUNDED_BY_ATTR,
  TEXT_BOUNDED_BY_NONE,
  TEXT_CEILING_ATTR,
  TEXT_CEILING_FROM_ATTR,
  stampKeptCeiling,
  TEXT_CONTENT_HEIGHT_ATTR,
  TEXT_FACES_PENDING_ATTR,
  TEXT_FACES_SETTLED_ATTR,
  TEXT_FIT_PASSES_ATTR,
  TEXT_ITEM_STATE_ATTR,
  TEXT_LATE_FACE_REFITS_ATTR,
  TEXT_LOAD_FIT_ATTR,
  TEXT_LOAD_HEIGHT_ATTR,
  TEXT_MEASURED_AT_ONE_ATTR,
  TEXT_RUN_LINE_HEIGHT_ATTR,
  TEXT_STYLES_SETTLED_ATTR,
  TEXT_WRITES_ATTR,
  itemRectAttribute,
} from '@/utils/editor/textGeometryAttributes'
import {
  settleTextBoxFaces,
  stampTextBoxFacesFailed,
  textBoxFaceWaitMs,
  textBoxFacesOutcome,
  watchTextBoxLateFaces,
} from '@/utils/editor/textBoxFaces'
import {
  settleTextBoxStyles,
  tallestLinePitchPx,
  tallestRunLineHeight,
  tallestRunLineHeightStamp,
  textBoxStylesOutcomeStamp,
} from '@/utils/editor/textBoxStyles'
import {
  bodyAnatomyStamp,
  bodyBlocksStamp,
  paintedContentHeight,
} from '@/utils/editor/textBoxPaint'
import { withoutActiveHtml } from 'utils/tinyMCE/withoutActiveHtml'

const PageText = ({
  text: rawText,
  selected,
  dragDisabled: [, setDragDisabled] = [null, () => {}],
  internalHeight: [internalHeight, setInternalHeight] = [0, () => {}],
  internalWidth: [internalWidth] = [0, () => {}],
  setItemHeight,
  multiSelectMode,
  itemRef,
  id,
  assetLocked,
  resizingState,
  environment,
  convertedToEditableState: [, setConvertedToEditable] = [false, () => {}],
  controlledX,
  controlledY,
  type,
  // The preview and the PDF hand every box the page the room under it is
  // computed from (the items they paint, the margins, the page size and
  // padding); on the canvas the hook reads the same from the editor's atoms.
  pageGeometry = null,
  // The preview's report (the page's ids for a log line); null elsewhere.
  bleedReport = null,
  ...props
}) => {
  countEditorProbeRender('PageText')
  useEditorProbeLifetime('PageText')
  const { x, y, width, height, editorTextDimensions } = props

  const { t } = useTranslation()
  const TinyMCERef = useRef(null)
  const textContainerRef = useRef(null)
  const fitPassesRef = useRef(0)
  const lateFaceRefitsRef = useRef(0)
  // Whether the load in flight is a late face's refit, and whether the
  // author has written to the text since its load result.
  const lateFaceRefitRef = useRef(false)
  const editedSinceLoadRef = useRef(false)
  const appliedSourceRef = useRef('')
  const writesRef = useRef(0)
  const measuredAtOneRef = useRef(null)
  // The tallest run's computed line-height and font-size at the instant the
  // load result read the body: a ratio other than the content rule's 1 is a
  // result taken under a late style, seen or not by the styles wait.
  const runLineHeightRef = useRef(null)
  const blocksRef = useRef(null)
  const anatomyRef = useRef(null)
  // The frame the ceiling was measured from, stamped beside it: two
  // surfaces disagreeing by a pixel name their sources instead of implying
  // them.
  const ceilingFromRef = useRef(null)
  // Where the box's current text stands with its one load result: `pending`
  // until faces have landed and the result is computed, `painting` while the
  // commit that shows it is in flight, `done` once it has painted. Height
  // writes from TinyMCE's own SetContent are held off until `done`, so the
  // initial content, a re-applied text and the fitted text never write a
  // height of their own over the result.
  const loadPhaseRef = useRef('pending')
  const loadResultRef = useRef(null)
  const loadStoredHeightRef = useRef(0)
  const [loadGeneration, setLoadGeneration] = useState(0)
  const [boundedHeight, setBoundedHeight] = useState(null)
  // The load result as the warning needs it, in state so the overlay's
  // control state follows it: whether the text was made smaller, and what
  // stopped the box.
  const [loadResultForWarning, setLoadResultForWarning] = useState(null)
  const stampTextFit = () => {
    const container = textContainerRef.current
    if (!container) return
    container.setAttribute(TEXT_FIT_PASSES_ATTR, String(fitPassesRef.current))
    container.setAttribute(
      TEXT_LATE_FACE_REFITS_ATTR,
      String(lateFaceRefitsRef.current),
    )
    container.setAttribute(TEXT_WRITES_ATTR, String(writesRef.current))
    // The readings belong to the current text: cleared with it, stamped
    // once its result lands.
    const measuredAtOne = measuredAtOneRef.current
    if (measuredAtOne) {
      container.setAttribute(
        TEXT_CONTENT_HEIGHT_ATTR,
        String(measuredAtOne.painted),
      )
      container.setAttribute(
        TEXT_MEASURED_AT_ONE_ATTR,
        String(measuredAtOne.measured),
      )
    } else {
      container.removeAttribute(TEXT_CONTENT_HEIGHT_ATTR)
      container.removeAttribute(TEXT_MEASURED_AT_ONE_ATTR)
    }
    const runLineHeight = tallestRunLineHeightStamp(runLineHeightRef.current)
    if (runLineHeight !== null) {
      container.setAttribute(TEXT_RUN_LINE_HEIGHT_ATTR, runLineHeight)
    } else {
      container.removeAttribute(TEXT_RUN_LINE_HEIGHT_ATTR)
    }
    if (blocksRef.current !== null) {
      container.setAttribute(TEXT_BLOCKS_ATTR, blocksRef.current)
    } else {
      container.removeAttribute(TEXT_BLOCKS_ATTR)
    }
    if (anatomyRef.current !== null) {
      container.setAttribute(TEXT_ANATOMY_ATTR, anatomyRef.current)
    } else {
      container.removeAttribute(TEXT_ANATOMY_ATTR)
    }
    const loadResult = loadResultRef.current
    if (loadResult) {
      container.setAttribute(TEXT_LOAD_HEIGHT_ATTR, String(loadResult.height))
      container.setAttribute(
        TEXT_CEILING_ATTR,
        loadResult.ceiling === null
          ? TEXT_BOUNDED_BY_NONE
          : String(loadResult.ceiling),
      )
      container.setAttribute(
        TEXT_BOUNDED_BY_ATTR,
        loadResult.boundedBy ?? TEXT_BOUNDED_BY_NONE,
      )
      container.setAttribute(
        TEXT_LOAD_FIT_ATTR,
        textBoxLoadFitStamp(loadResult),
      )
      const from = ceilingFromRef.current
      if (from) {
        container.setAttribute(
          TEXT_CEILING_FROM_ATTR,
          itemRectAttribute(from.x, from.y, from.width, null),
        )
      } else {
        container.removeAttribute(TEXT_CEILING_FROM_ATTR)
      }
      stampKeptCeiling(container, loadResult.kept ?? null)
    } else {
      container.removeAttribute(TEXT_LOAD_HEIGHT_ATTR)
      container.removeAttribute(TEXT_CEILING_ATTR)
      container.removeAttribute(TEXT_BOUNDED_BY_ATTR)
      container.removeAttribute(TEXT_LOAD_FIT_ATTR)
      container.removeAttribute(TEXT_CEILING_FROM_ATTR)
      stampKeptCeiling(container, null)
    }
  }

  const [text, setText] = useState('')
  const [appliedTextVersion, setAppliedTextVersion] = useState(0)
  const [editing, setEditing] = useState(false)
  const editingRef = useRef(editing)
  editingRef.current = editing
  const [itemState, setItemState] = useState('loading')
  const [tinyMCERefTrigger, setTinyMCERefTrigger] = useState(null)
  const [insertedTemplateTrigger, setInsertedTemplateTrigger] = useState(null)
  const [contentSizeAtStartOfResize, setContentSizeAtStartOfResize] = useState({
    width: 0,
    height: 0,
    hasResized: false,
  })
  const [hasTextCaseDetailAttribute, setHasTextCaseDetailAttribute] =
    useState(false)
  const [showConvertToTextModal, setShowConvertToTextModal] = useState(false)

  const caseDetails = useRecoilValue(caseSelector)
  const funeralHomeDetails = useRecoilValue(caseFuneralHomeAtom)
  const dateFormat = useRecoilValue(dateFormatAtom)
  const activeMenu = useRecoilValue(rightMenuAtom)
  const pageSetLocale = useRecoilValue(pageSetLocaleAtom)
  const selectedTheme = useRecoilValue(selectedThemeAtom)
  const swapServiceTemplateSignal = useRecoilValue(
    swapServiceTemplateSignalAtom,
  )
  const [showConvertTextModalForItemId, setShowConvertTextModalForItemId] =
    useRecoilState(showConvertTextModalForItemIdAtom)
  const [
    showServiceTemplateModalForItemId,
    setShowServiceTemplateModalForItemId,
  ] = useRecoilState(showServiceTemplateModalForItemIdAtom)
  const [swapServiceTemplateForItemId, setSwapServiceTemplateForItemId] =
    useRecoilState(swapServiceTemplateForItemIdAtom)

  const setThisItemTextValue = useSetRecoilState(
    itemSelector({ id: id, path: 'text.value' }),
  )
  const setThisItemEditorTextDimensions = useSetRecoilState(
    itemSelector({ id: id, path: 'editorTextDimensions', annotation: true }),
  )
  // The editor's load result, saved into the row beside the height it
  // wrote: the PDF worker holds its own load result against it, so an
  // editor-versus-PDF difference is caught without a new payload.
  const setThisItemTextLoad = useSetRecoilState(
    itemSelector({ id: id, path: 'textLoad', annotation: true }),
  )
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)

  const setToast = useSetToast()
  const isEditorEnvironment = environment?.trim?.() === 'editor'

  const {
    templateName: serviceInformationTemplateName,
    setTemplateName: setServiceInformationTemplateName,
    showModal: showServiceInformationTemplateModal,
    setShowModal: setShowServiceInformationTemplateModal,
    createdTemplateContent,
    handleCreateTemplate: handleCreateServiceTemplate,
    handleSwapTemplate,
    isServiceInformationTemplate,
  } = useServiceTemplates({
    editorRef: TinyMCERef,
  })

  // Calculate actual text content bounds for accurate bleed detection
  const { adjustedX, adjustedY, adjustedWidth, adjustedHeight } =
    useTextContentBounds({
      TinyMCERef,
      containerWidth: internalWidth || width || 0,
      containerHeight: internalHeight || height || 0,
      itemState,
    })

  // A margin warning speaks for a Lotus text box alone, so this item takes
  // only the load result's phrase from the hook.
  const { loadWarning } = useBleedWarning({
    itemX: (controlledX || x || 0) + adjustedX,
    itemY: (controlledY || y || 0) + adjustedY,
    itemType: type,
    assetLocked: assetLocked,
    loadResult: loadResultForWarning,
  })

  // Register text-specific control state with the overlay
  const textControlState = useMemo(
    () => ({
      text: {
        hasTextCaseDetailAttribute,
        isServiceInformationTemplate: isServiceInformationTemplate(),
      },
      warnings: {
        loadWarningMessage: isEditorEnvironment
          ? (loadWarning?.message ?? null)
          : null,
        loadWarningKey: isEditorEnvironment ? (loadWarning?.key ?? null) : null,
        textContentBounds: {
          adjustedX,
          adjustedY,
          adjustedWidth,
          adjustedHeight,
        },
      },
    }),
    [
      hasTextCaseDetailAttribute,
      isServiceInformationTemplate,
      loadWarning,
      isEditorEnvironment,
      adjustedX,
      adjustedY,
      adjustedWidth,
      adjustedHeight,
    ],
  )

  useItemControlState(id, textControlState)

  const { unlockNonEditableBlocks, removeDataAttributes } = useMemo(
    () => toggleEditableBlocks(TinyMCERef?.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [TinyMCERef.current],
  )

  const statusProps = useMemo(
    () => ({
      x,
      y,
      width,
      height,
      itemState,
      environment,
      hasContent: !!text.trim(),
    }),
    [x, y, width, height, itemState, environment, text],
  )

  const mergeInputsRef = useRef(null)
  mergeInputsRef.current = {
    caseDetails,
    funeralHomeDetails,
    dateFormat,
    pageSetLocale,
    environment,
    selectedTheme,
  }
  const storedTextRef = useRef('')
  storedTextRef.current = rawText?.value ?? ''
  const lastAppliedRef = useRef(null)

  const mergeDynamicText = useCallback((html) => {
    /* Ensure empty text boxes contain at least one space character wrapped in tinyMCE allowed html tags */
    if (html.trim() === '') return '<p>&nbsp;</p>'
    const merged = sanitizeDynamicText({
      html: updateDynamicText({ initialHtml: html, ...mergeInputsRef.current }),
      environment: mergeInputsRef.current.environment,
    })
    return withoutActiveHtml(merged)
  }, [])

  // `lateFace` restarts the current text's load for a face that landed after
  // its result: the text paints again as applied, not as that result fitted
  // it, and the new result is counted apart from the load passes.
  const applyText = useCallback(
    (html, { autosize = true, via = 'text', lateFace = false } = {}) => {
      const updatedText = mergeDynamicText(html)
      const stored = storedTextRef.current
      const lastApplied = lastAppliedRef.current
      const shown =
        lastApplied === null
          ? ''
          : lastApplied.stored === stored
            ? lastApplied.output
            : stored
      const changed = updatedText !== shown
      countEditorProbeRender(
        `PageText.applyText:${String(id).slice(0, 8)}:changed=${changed}:via=${via}`,
      )
      if (!changed && !lateFace) return false
      // The restarted text is the load's paint, never an edit to store.
      if (lateFace) loadPhaseRef.current = 'pending'
      setText(updatedText)
      const wrote = setContent(updatedText)
      lastAppliedRef.current = {
        stored: wrote ? updatedText : stored,
        output: updatedText,
      }
      if (autosize) {
        // New text, no result yet: the pass count starts over for it, the
        // previous text's readings are dropped, and no height is written
        // until its own result has painted.
        if (lateFace) lateFaceRefitRef.current = true
        else {
          fitPassesRef.current = 0
          lateFaceRefitsRef.current = 0
        }
        appliedSourceRef.current = html
        editedSinceLoadRef.current = false
        loadPhaseRef.current = 'pending'
        measuredAtOneRef.current = null
        runLineHeightRef.current = null
        blocksRef.current = null
        anatomyRef.current = null
        ceilingFromRef.current = null
        loadResultRef.current = null
        setLoadResultForWarning(null)
        stampTextFit()
        setAppliedTextVersion((version) => version + 1)
      }
      return true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, mergeDynamicText],
  )

  const applyResizedText = useCallback(
    (html) => applyText(html, { autosize: false, via: 'autosize' }),
    [applyText],
  )

  const dependencies = useMemo(
    () => dynamicTextDependencies(rawText?.value),
    [rawText?.value],
  )
  const inputsSignature = useMemo(
    () =>
      dynamicTextInputsSignature(
        { caseDetails, funeralHomeDetails, dateFormat },
        dependencies,
      ),
    [caseDetails, funeralHomeDetails, dateFormat, dependencies],
  )
  const appliedInputsRef = useRef(null)

  const autosizeProps = useCallback(
    (isFirstLoad) => ({
      itemState,
      setItemState,
      resizingState,
      internalWidth: internalWidth || width,
      internalHeight: isFirstLoad ? height : internalHeight,
      TinyMCERef,
      tinyMCERefTrigger,
      applyText: applyResizedText,
      editorTextDimensions,
      environment,
      orderId: props?.orderId || null,
      pageSetId: props?.pageSetId || null,
      // Only the load result records the measured-vs-painted reading; the
      // author's own resize fits already-fitted text and proves nothing.
      onMeasuredAtOne: isFirstLoad
        ? (reading) => {
            measuredAtOneRef.current = reading
          }
        : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      itemState,
      resizingState,
      internalWidth,
      width,
      internalHeight,
      height,
      TinyMCERef,
      tinyMCERefTrigger,
      applyResizedText,
    ],
  )
  // The load result settles after the faces land, in a later tick than the
  // render that scheduled it; it reads the box's fit props as they are then.
  const loadInputsRef = useRef(null)
  loadInputsRef.current = { x, y, width, height, autosizeProps }
  // On the canvas the box's own rect comes off the same store snapshot the
  // ceiling hook reads its neighbours from, in the same instant: a box the
  // author moved while its faces were landing is bounded from where it is,
  // against neighbours where they are. Off the canvas the props are the
  // page JSON and never move.
  const readStoredRect = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(itemAtomFamily(id)).valueMaybe() ?? null,
    [id],
  )

  // In the editor, records the item's frame and its TinyMCE body's size and
  // text as its editorTextDimensions (debounced, refreshed by the
  // useDebugEffect below); on the PDF, when the load result shrinks the text
  // into its frame, fitTextIntoBox (hooks/editor/useTextAutosize) compares the
  // four sizes with its own and logs a text_discrepancy when any differs.
  // A debugging aid for the editor and the PDF disagreeing on a TinyMCE box's
  // size. When that is settled it is removed with every reader and writer of
  // editorTextDimensions: the useDebugEffect call below and
  // hooks/debug/useDebugEffect.ts, which nothing else calls; the
  // setThisItemEditorTextDimensions setter above; the prop this component
  // reads and passes to fitTextIntoBox; verifySizeDiscrepancies in
  // hooks/editor/useTextAutosize.js and checkDiscrepancies in
  // utils/debug/textSizesDiscrepancies.ts; the field packages/test-data's
  // tinymceTextItem writes and baseline-content.unit.test.ts asserts; and the
  // tinymce-audit pages that describe it. The EditorTextDimensions type in
  // types/editor.ts goes too: nothing imports it. Saved pages keep the field
  // in their JSON, as the generated test-data templates do; nothing reads it
  // once these go.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateDimensions = useCallback(
    debounce(() => {
      if (!TinyMCERef?.current) return

      if (isEditorEnvironment) {
        const contentWidth = TinyMCERef?.current?.bodyElement?.offsetWidth
        const contentHeight = TinyMCERef?.current?.bodyElement?.offsetHeight
        const textContent = TinyMCERef?.current?.getContent()

        setThisItemEditorTextDimensions({
          editorWidth: width,
          editorHeight: height,
          editorContentWidth: contentWidth,
          editorContentHeight: contentHeight,
          editorText: textContent,
        })
      }
    }, 150),
    [width, height, environment, TinyMCERef?.current],
  )

  const validateCaseDetailAttribute = () => {
    const hasTinyMCE = TinyMCERef?.current
    const canValidateCaseDetailAttribute = text && hasTinyMCE
    if (!canValidateCaseDetailAttribute) return

    const textBody = TinyMCERef?.current?.getBody?.()
    const hasCaseDetail = hasCaseDetailAttribute(textBody)
    setHasTextCaseDetailAttribute(hasCaseDetail)
  }

  const convertToEditable = useCallback(() => {
    const canConvertToEditable = TinyMCERef?.current
    if (!canConvertToEditable) return

    unlockNonEditableBlocks()
    removeDataAttributes()
    setHasTextCaseDetailAttribute(false)
    setConvertedToEditable(true)
    setToast({
      text: t('toast.textBoxIsNowEditable'),
      icon: 'info',
      timeout: 10,
    })
  }, [
    setConvertedToEditable,
    setToast,
    unlockNonEditableBlocks,
    removeDataAttributes,
    t,
  ])

  useDebugEffect(() => {
    updateDimensions()
  }, [width, height, TinyMCERef])

  useEffect(() => {
    if (!insertedTemplateTrigger) return
    setHasTextCaseDetailAttribute(true)
  }, [insertedTemplateTrigger])

  // This runs after the service template is created. applyText is in charge of styling the template with the theme styles.
  useEffect(() => {
    if (!createdTemplateContent) return
    applyText(createdTemplateContent, { via: 'template' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdTemplateContent])

  useEffect(() => {
    validateCaseDetailAttribute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, itemState, tinyMCERefTrigger])

  useEffect(() => {
    const applied = appliedInputsRef.current
    appliedInputsRef.current = {
      dependencies,
      signature: inputsSignature,
      swapSignal: swapServiceTemplateSignal,
    }
    const storedTextChangedAlone =
      applied !== null &&
      applied.dependencies !== dependencies &&
      applied.swapSignal === swapServiceTemplateSignal &&
      dynamicTextInputsSignature(
        { caseDetails, funeralHomeDetails, dateFormat },
        applied.dependencies,
      ) === applied.signature
    if (storedTextChangedAlone) return
    applyText(rawText?.value ?? '', {
      via: applied === null ? 'load' : 'inputs',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsSignature, swapServiceTemplateSignal])

  useEffect(() => {
    setDragDisabled(editing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  // Listen for convert-to-text modal trigger from ItemControlOverlay
  useEffect(() => {
    if (showConvertTextModalForItemId === id) {
      setShowConvertToTextModal(true)
      setShowConvertTextModalForItemId(null)
    }
  }, [showConvertTextModalForItemId, id, setShowConvertTextModalForItemId])

  // Listen for service template modal trigger from ItemControlOverlay
  useEffect(() => {
    if (showServiceTemplateModalForItemId === id) {
      setShowServiceInformationTemplateModal(true)
      setShowServiceTemplateModalForItemId(null)
    }
  }, [
    showServiceTemplateModalForItemId,
    id,
    setShowServiceTemplateModalForItemId,
    setShowServiceInformationTemplateModal,
  ])

  // Listen for swap service template trigger from ItemControlOverlay
  useEffect(() => {
    if (swapServiceTemplateForItemId === id) {
      handleSwapTemplate()
      setSwapServiceTemplateForItemId(null)
    }
  }, [
    swapServiceTemplateForItemId,
    id,
    setSwapServiceTemplateForItemId,
    handleSwapTemplate,
  ])

  // Writes the painted content height into the item. `origin` names what
  // asked: `'edit'` for the author's own keystroke, blur, toolbar command or
  // kerning, `'content'` for TinyMCE's SetContent (the initial content, a
  // programmatic reset, an insert at the caret). Nothing writes while the
  // current text's load result is owed or in flight: that result is the one
  // height the box takes on load, on every surface. The height write is an
  // annotation, so on the canvas an `'edit'` marks the page unsaved itself,
  // held or not: the text is committed to the item only on blur, and Back
  // and the leave prompt read the mark while the caret is still in the box.
  const updateHeight = useCallback(
    (origin = 'edit') => {
      if (!TinyMCERef?.current) return

      const updatedHeight = TinyMCERef.current.bodyElement?.offsetHeight
      const held = loadPhaseRef.current !== 'done'
      countEditorProbeRender(
        `PageText.updateHeight:${String(id).slice(0, 8)}:${origin}:${updatedHeight || 0}:${held ? 'held' : 'wrote'}`,
      )
      if (origin === 'edit' && isEditorEnvironment) setUnsavedChanges(true)
      if (held) return
      if (updatedHeight) {
        setItemHeight?.(updatedHeight)
        setInternalHeight?.(updatedHeight)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [TinyMCERef?.current],
  )

  useEffect(() => {
    const isTextItemSelected = selected
    if (isTextItemSelected) return
    setEditing(false)
  }, [selected])

  // The one room both kinds of box record: on the canvas it reads the
  // editor's atoms at fit time (a neighbour the author moved bounds by its
  // current edge); on the preview and the PDF it reads the page handed down.
  const growthCeilingOf = useEditorTextBoxGrowthCeiling(
    isEditorEnvironment ? null : pageGeometry,
  )

  // The load result: once the faces the text uses and the content styles
  // have landed, the box paints at the row's frame and the text is fitted
  // into it — text past the frame by no more than the grace keeps its size,
  // text further past it is shrunk into the frame. The room the page leaves
  // under the box (the margin, the fold, the nearest text or QR item below
  // it) rides along as a record: a stored frame past it is flagged
  // `preexisting`. Exactly once per text, the same on every surface, and a
  // paint on every surface: the editor's wrapper takes the height, the
  // preview and the PDF give it to the container, and the row keeps its
  // frame and its sizes.
  const settleLoad = () => {
    const editor = TinyMCERef.current
    const body = editor?.bodyElement
    if (!editor || !body) return
    countEditorProbeRender(`PageText.autosize:${String(id).slice(0, 8)}`)
    const inputs = loadInputsRef.current
    const rect = (isEditorEnvironment ? readStoredRect() : null) ?? inputs
    // The painted height without TinyMCE's own scaffolding (a caret
    // container, a bogus trailing paragraph), which never prints; the
    // blocks the body holds at this instant, stamped, so an extra line box
    // that is gone by the time anyone looks is named.
    const contentHeight = paintedContentHeight(body)
    blocksRef.current = bodyBlocksStamp(body)
    anatomyRef.current = bodyAnatomyStamp(body)
    runLineHeightRef.current = tallestRunLineHeight(body)
    const storedHeight = pxOf(rect.height)
    loadStoredHeightRef.current = storedHeight
    // One line of the box's text goes with the frame, recorded beside the
    // room for a stored frame past it.
    const ceiling = growthCeilingOf({
      id,
      x: pxOf(rect.x),
      y: pxOf(rect.y),
      width: pxOf(rect.width),
      height: storedHeight,
      lineHeight: tallestLinePitchPx(runLineHeightRef.current),
    })
    ceilingFromRef.current = ceiling?.measuredFrom ?? null
    const result = textBoxLoadHeight({
      contentHeight,
      storedHeight,
      ceiling,
    })
    if (result.fitted) {
      fitTextIntoBox({
        ...inputs.autosizeProps(true),
        internalHeight: result.height,
        strict: true,
      })
    } else {
      measuredAtOneRef.current = {
        measured: measureTextDimensions({ html: editor.getContent(), body })
          .height,
        painted: contentHeight,
      }
      setItemState('idle')
    }
    loadResultRef.current = result
    setLoadResultForWarning({
      fitted: result.fitted,
      boundedBy: result.boundedBy,
      overflowing: false,
    })
    if (bleedReport)
      reportPreviewLoadWarning(id, 'tinymce', result, bleedReport)
    if (isEditorEnvironment)
      setThisItemTextLoad({
        height: result.height,
        ceiling: result.ceiling,
        boundedBy: result.boundedBy,
        kept: result.kept ?? null,
        fit: textBoxLoadFitStamp(result),
        fontSizesPt: fontSizesPtOf(editor.getContent()),
        at: Date.now(),
      })
    // The result is a paint: the wrapper and the container take it, the
    // item keeps the row's frame. Nothing here reaches the atom's height,
    // so a Save after a load sends the frame the row already holds, and
    // every load of every surface starts from the same row.
    setInternalHeight?.(result.height)
    setBoundedHeight(result.height)
    logTextBoxLoadResult({
      itemId: String(id),
      kind: 'tinymce',
      surface: environment ?? 'editor',
      storedHeight,
      paintedHeight: result.height,
      ceiling: result.ceiling,
      boundedBy: result.boundedBy,
      fitted: result.fitted,
      overflowing: false,
      kept: result.kept ?? null,
    })
    if (lateFaceRefitRef.current) lateFaceRefitsRef.current += 1
    else fitPassesRef.current += 1
    lateFaceRefitRef.current = false
    stampTextFit()
    loadPhaseRef.current = 'painting'
    setLoadGeneration((generation) => generation + 1)
  }

  // The commit that paints the result has landed — TinyMCE's SetContent for
  // a fitted text fired inside it and was held — so the author's edits may
  // write heights again. A fitted text is then measured as it painted: the
  // loop fits the HTML it measures, but the HTML it applies goes through
  // the dynamic-text merge, and a case detail that pins its size (an
  // obituary's `data-persistent-font-size`) comes back at that size inside
  // the shrunk line height. Text still past the box by more than the grace
  // is the last resort: the box is overflowing, which the worker records
  // (`content-overflowing`) and the load log records at info.
  useLayoutEffect(() => {
    if (loadPhaseRef.current !== 'painting') return
    loadPhaseRef.current = 'done'
    const result = loadResultRef.current
    const body = TinyMCERef.current?.bodyElement
    if (!result?.fitted || !body) return
    const textHeight = paintedContentHeight(body)
    if (textHeight - result.height <= GRACE_DISTANCE) return
    setItemState('content-overflowing')
    setLoadResultForWarning({
      fitted: true,
      boundedBy: result.boundedBy,
      overflowing: true,
    })
    logTextBoxLoadResult({
      itemId: String(id),
      kind: 'tinymce',
      surface: environment ?? 'editor',
      storedHeight: loadStoredHeightRef.current,
      paintedHeight: result.height,
      textHeight,
      ceiling: result.ceiling,
      boundedBy: result.boundedBy,
      fitted: true,
      overflowing: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGeneration])

  // The loop rescales whatever sizes the box holds, so a pass under a
  // fallback face would be rescaled again once the real face lands and never
  // recover the size a clean load reaches: the faces the text uses are asked
  // for first, and the box stays loading until they have landed or failed.
  // A face that lands after the result restarts the load from the applied
  // text; a box the author is typing in, or has written to since, keeps
  // what they typed.
  useEffect(() => {
    if (!tinyMCERefTrigger) return
    let cancelled = false
    let stopLateFaces = () => {}
    // Answers false while the load has not measured yet, so the watch stays
    // on for a face that lands after the result.
    const refitForLateFace = () => {
      if (cancelled) return true
      if (loadPhaseRef.current === 'pending') return false
      if (editingRef.current || editedSinceLoadRef.current) return true
      if (TinyMCERef.current?.isDirty?.()) return true
      applyText(appliedSourceRef.current, { via: 'late-face', lateFace: true })
      return true
    }
    const fit = () => {
      if (cancelled) return
      // What the settle concluded, stamped before the result so a row
      // fitted under a fallback names the face that never came.
      const settled = TinyMCERef.current?.bodyElement
      if (!settled) {
        settleLoad()
        return
      }
      stopLateFaces = watchTextBoxLateFaces(settled, refitForLateFace)
      const outcome = textBoxFacesOutcome(settled)
      textContainerRef.current?.setAttribute(
        TEXT_FACES_SETTLED_ATTR,
        outcome.status === 'loaded'
          ? 'loaded'
          : `${outcome.status}:${outcome.families.join(',')}`,
      )
      if (textContainerRef.current)
        stampTextBoxFacesFailed(textContainerRef.current, settled, environment)
      // The body's content styles are the other input the result depends
      // on: a body the rule has not reached lays every line out at its
      // face's `normal`, taller than it will paint. Waited for like the
      // faces, and stamped.
      const stylesPending = settleTextBoxStyles(settled)
      if (!stylesPending) {
        textContainerRef.current?.setAttribute(
          TEXT_STYLES_SETTLED_ATTR,
          textBoxStylesOutcomeStamp(null),
        )
        settleLoad()
        return
      }
      countEditorProbeRender(`PageText.stylesPending:${String(id).slice(0, 8)}`)
      void stylesPending.then((styles) => {
        if (cancelled) return
        textContainerRef.current?.setAttribute(
          TEXT_STYLES_SETTLED_ATTR,
          textBoxStylesOutcomeStamp(styles),
        )
        settleLoad()
      })
    }
    const body = TinyMCERef.current?.bodyElement
    const pending = body
      ? settleTextBoxFaces(body, textBoxFaceWaitMs(environment))
      : null
    if (!pending) {
      fit()
      return () => stopLateFaces()
    }
    setItemState('loading')
    textContainerRef.current?.setAttribute(TEXT_FACES_PENDING_ATTR, 'true')
    countEditorProbeRender(`PageText.facesPending:${String(id).slice(0, 8)}`)
    void pending.then(() => {
      textContainerRef.current?.removeAttribute(TEXT_FACES_PENDING_ATTR)
      fit()
    })
    return () => {
      cancelled = true
      stopLateFaces()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedTextVersion, tinyMCERefTrigger])

  // The author's own resize fits the text into the box they drew; it is not
  // a load result, so the pass count does not move.
  useEffect(() => {
    resizeObserver(autosizeProps(false))
    if (resizingState === 'stop') stampTextFit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizingState, TinyMCERef?.current])

  const setContent = (textValue = null) => {
    if (!TinyMCERef?.current) return false

    const content = textValue || TinyMCERef.current.getContent()
    const stored = storedTextRef.current
    countEditorProbeRender(
      `PageText.setContent:${String(id).slice(0, 8)}:same=${content === stored}`,
    )
    if (content === stored) return false
    // A write while the current text's load result is owed is that result's
    // own — the merge for the current case, the loop fitting the text into
    // the row's frame. It paints (TinyMCE holds it) and goes no further: the
    // item keeps the row's text, so the sizes the rule found are never
    // stored on its behalf. The first edit the author makes in the box
    // commits what TinyMCE holds, sizes included, as their own.
    const asLoadResult = loadPhaseRef.current === 'pending'
    if (!asLoadResult) {
      setThisItemTextValue(content)
      editedSinceLoadRef.current = true
    }
    writesRef.current += 1
    stampTextFit()
    return !asLoadResult
  }

  // A click in and out of an untouched box writes nothing: the height
  // follows the content only when TinyMCE reports an edit, since the painted
  // body is a load result's until then and the row's frame must not take it.
  const handleBlur = () => {
    if (!shouldCommitBlurContent(TinyMCERef?.current)) return
    setContent()
    updateHeight('edit')
  }

  const selectContent = () => {
    if (editing) return

    const editorBody = TinyMCERef?.current?.getBody()
    if (!editorBody) return

    const obituaryElement = editorBody.querySelector(
      '[data-case-detail="obituary"]',
    )
    if (obituaryElement) {
      TinyMCERef?.current?.editorCommands?.commands?.exec?.selectall(
        obituaryElement,
      )
    } else {
      TinyMCERef?.current?.editorCommands?.commands?.exec?.selectall()
    }
  }

  const handleDoubleClick = useCallback(() => {
    if (assetLocked || multiSelectMode) return
    selectContent()
    setEditing(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetLocked, multiSelectMode])

  const captureContentAtStartOfResize = useCallback(() => {
    const startingToResize = resizingState === 'start'
    const shouldSetContentSize = startingToResize && TinyMCERef?.current
    if (!shouldSetContentSize) return

    setContentSizeAtStartOfResize({
      width: TinyMCERef.current.bodyElement.offsetWidth,
      height: TinyMCERef.current.bodyElement.offsetHeight,
      hasResized: true,
    })
  }, [resizingState])

  useEffect(() => {
    if (!tinyMCERefTrigger) return
    captureContentAtStartOfResize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizingState, tinyMCERefTrigger])

  const setTinyMCERef = useCallback((ref) => {
    if (!ref) return
    TinyMCERef.current = ref
  }, [])

  // The Name Order dialog's arrangement is written to the case, the write
  // every mounted editor's customData refreshes from.
  const authorCaseFields = useAuthorCaseFields()
  const onNameArrangement = useCallback(
    (arrangement) =>
      authorCaseFields([
        { fieldKey: EDITOR_FIELD_NAME_OF_DECEASED, arrangement },
      ]),
    [authorCaseFields],
  )

  const tinyMCEProps = {
    probeLabel: isEditorEnvironment ? 'canvas' : `canvas-${environment}`,
    setToast: setToast,
    setRef: setTinyMCERef,
    setTinyMCERefTrigger,
    value: text,
    caseDetails,
    updateHeight,
    onKeyUp: () => updateHeight('edit'),
    onCaseDetailTemplateInserted: setInsertedTemplateTrigger,
    onNameArrangement,
  }

  const handleConvertToTextButtonClick = () => {
    convertToEditable()
    setShowConvertToTextModal(false)
  }

  return (
    <>
      <S.LoadingState {...statusProps} />
      {/* Only render portal in editor - prevents 50+ DOM portals in preview/print causing flash */}
      {isEditorEnvironment &&
        createPortal(
          <FullscreenModal
            show={[showConvertToTextModal, setShowConvertToTextModal]}
          >
            <Flex column full padding="32px" maxWidth="600px">
              <Text size="20px" weight="600" margin="10px 0 10px 0">
                {t('editor.pageItems.pageText.warning')}:
              </Text>
              <Text size="16px" margin="10px 0 10px 0">
                {t('editor.pageItems.pageText.warningText')}
              </Text>
              <Flex justify="flex-end" margin="10px 0 0 0">
                <Button
                  margin="0 16px 0 0"
                  onClick={() => setShowConvertToTextModal(false)}
                >
                  {t('editor.unsavedProgressModal.cancelButton')}
                </Button>
                <Button
                  primary
                  margin="0 16px 0 0"
                  onClick={() => handleConvertToTextButtonClick()}
                >
                  {t('editor.pageItems.pageText.confirm')}
                </Button>
              </Flex>
            </Flex>
          </FullscreenModal>,
          document.body,
        )}
      {/* Only render in editor - prevents modal per text item in preview/print */}
      {isEditorEnvironment && showServiceInformationTemplateModal && (
        <ServiceTemplateCreateModal
          show={showServiceInformationTemplateModal}
          setShow={setShowServiceInformationTemplateModal}
          templateName={serviceInformationTemplateName}
          setTemplateName={setServiceInformationTemplateName}
          onCreateTemplate={handleCreateServiceTemplate}
        />
      )}
      <S.Text
        ref={itemRef}
        selected={selected}
        editing={editing}
        boundedHeight={isEditorEnvironment ? null : boundedHeight}
        onDoubleClick={() => handleDoubleClick()}
        onBlur={handleBlur}
        {...props}
        data-text-item={id}
        data-item-rect={itemRectAttribute(x, y, width, height)}
      >
        <S.TextContainer
          ref={textContainerRef}
          editing={editing}
          cantHighlight={activeMenu !== 'Elements'}
          itemState={itemState}
          resizingState={resizingState}
          contentSizeAtStartOfResize={contentSizeAtStartOfResize}
          data-text-loading={itemState === 'loading'}
          {...{ [TEXT_ITEM_STATE_ATTR]: itemState }}
        >
          {text && <TinyMCE {...tinyMCEProps} />}
        </S.TextContainer>
      </S.Text>
    </>
  )
}

export default React.memo(PageText)
